import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { ClipboardX } from "lucide-react";

import {
  UIEmptyState,
  UIFormSectionCard,
  UIProgressBar,
  UIRating,
  UIStatusTag,
} from "../../../ui";
import messages from "../../../../assets/locale/messages";
import {
  ATTRIBUTE_NAME_TO_CRITERIA_KEY,
  SECTION_TO_GROUP_KEY,
} from "./constants";

// Groups the survey criteria by their `section`, preserving first-seen order.
const groupBySection = (attributes = []) => {
  const map = new Map();
  attributes.forEach((item) => {
    const section = item.section || "";
    if (!map.has(section)) map.set(section, []);
    map.get(section).push(item);
  });
  return [...map.entries()].map(([section, items]) => ({ section, items }));
};

const CompanyEvaluationResult = ({ survey }) => {
  const lang = useSelector((state) => state.locale.lang);
  const t = messages[lang].events.viewEvents.evaluationsTab.companyTab;

  if (!survey) {
    return (
      <UIEmptyState
        icon={<ClipboardX size={40} />}
        title={t.pendingTitle}
        description={t.pendingDescription}
      />
    );
  }

  const groups = groupBySection(survey.attributes_data);
  const percentage = Math.round(survey.score_percentage ?? 0);

  return (
    <Box className="company-eval-result">
      <UIFormSectionCard>
        <Box className="company-eval-result__satisfaction-row">
          <Box className="company-eval-result__satisfaction-left">
            <Typography className="company-eval-result__satisfaction-label">
              {t.overallSatisfaction}
            </Typography>
            <Typography className="company-eval-result__satisfaction-pct">
              {percentage}%
            </Typography>
          </Box>

          <Box className="company-eval-result__bar-wrapper">
            <UIProgressBar value={percentage} showDot />
            <Box className="company-eval-result__bar-labels">
              <Typography className="company-eval-result__bar-label">0%</Typography>
              <Typography className="company-eval-result__bar-label">50%</Typography>
              <Typography className="company-eval-result__bar-label">100%</Typography>
            </Box>
          </Box>

          <Box className="company-eval-result__status-col">
            <Typography className="company-eval-result__satisfaction-label">
              {t.evaluationStatus}
            </Typography>
            <UIStatusTag
              status="completed"
              label={t.statusCompleted}
              tone="successSoft"
              preset="table"
              sx={{
                borderRadius: "9999px",
                border: "1px solid",
                height: "auto",
                "& .MuiChip-label": { padding: "4px 12px" },
              }}
            />
          </Box>
        </Box>
      </UIFormSectionCard>

      <Box className="company-eval-result__criteria-grid">
        {groups.map((group) => (
          <UIFormSectionCard
            key={group.section}
            title={
              t.criteriaGroups[SECTION_TO_GROUP_KEY[group.section]] ?? group.section
            }
          >
            <Box className="company-eval-result__criteria-list">
              {group.items.map((item) => (
                <Box
                  key={item.attribute_name}
                  className="company-eval-result__criteria-item"
                >
                  <Typography className="company-eval-result__criteria-label">
                    {t.criteria[ATTRIBUTE_NAME_TO_CRITERIA_KEY[item.attribute_name]] ??
                      item.attribute_name}
                  </Typography>
                  <UIRating value={item.rating} />
                </Box>
              ))}
            </Box>
          </UIFormSectionCard>
        ))}
      </Box>

      <UIFormSectionCard title={t.suggestionsFeedback}>
        <Typography className="company-eval-result__feedback-text">
          {survey.comments || t.noFeedback}
        </Typography>
      </UIFormSectionCard>
    </Box>
  );
};

CompanyEvaluationResult.propTypes = {
  survey: PropTypes.shape({
    score_percentage: PropTypes.number,
    comments: PropTypes.string,
    attributes_data: PropTypes.arrayOf(
      PropTypes.shape({
        section: PropTypes.string,
        attribute_name: PropTypes.string,
        rating: PropTypes.number,
      }),
    ),
  }),
};

CompanyEvaluationResult.defaultProps = {
  survey: null,
};

export default CompanyEvaluationResult;
